import { useEffect, useState } from "react";

import useAPI from "cuanto/package/hooks/APIContext";
import { captureException } from "cuanto/services/Sentry.web";

import { CatalogueData, Price } from "../types";

type PriceData = {
  prices: Price[];
  show_savings_across_prices: boolean;
};

export default function useCatalogueProduct(
  username: string,
  short_uuid: string
) {
  const api = useAPI();

  const [catalogueData, setCatalogueData] = useState<CatalogueData | null>(
    null
  );

  const [hasCatalogueDataLoaded, setHasCatalogueDataLoaded] = useState(false);
  const [hasPricesLoaded, setHasPricesLoaded] = useState(false);
  const [priceData, setPriceData] = useState<PriceData | null>(null);

  useEffect(() => {
    async function getCatalogueProduct() {
      try {
        const { data } = await api.get<CatalogueData>(
          `/${username}/products/${short_uuid}`
        );
        setCatalogueData(data);
      } catch (e) {
        captureException(e);
      } finally {
        setHasCatalogueDataLoaded(true);
      }
    }

    async function getPrices() {
      try {
        const { data } = await api.get<PriceData>(
          `/ecommerce/${username}/products/${short_uuid}/prices`
        );
        setPriceData(data);
      } catch (e) {
        captureException(e);
      } finally {
        setHasPricesLoaded(true);
      }
    }

    getCatalogueProduct();
    getPrices();
  }, [username, short_uuid, api]);

  const isLoading = !hasCatalogueDataLoaded || !hasPricesLoaded;

  if (isLoading || !catalogueData || !priceData) {
    return { isLoading };
  }

  const { products, merchant } = catalogueData;

  const product = products.find((product) => product.short_uuid === short_uuid);
  const productVariants = products.filter((p) => p.product_group_uuid !== null);

  const storeClosed = catalogueData.settings.some(
    (s) => s.key === "store_closed" && s.value === "t"
  );

  const prices = priceData.prices;
  const showPriceSavings = priceData.show_savings_across_prices;

  return {
    isLoading,
    product,
    productVariants,
    prices,
    showPriceSavings,
    merchant,
    storeClosed,
  };
}
